import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { fetchDeployedVersion } from '@/lib/appUpdate';

const CHECK_INTERVAL_MS = 5 * 60_000;
const UPDATE_TOAST_ID = 'app-update-available';

/**
 * Detects a new web deploy while a tab is open and gets the user onto it without yanking the
 * page out from under them. Renders nothing.
 *
 * Detection: poll /version.json (see appUpdate.ts) every 5 minutes, plus immediately when the
 * tab becomes visible again — the common case is a tab left open overnight across a deploy,
 * and the refocus check catches it in the first second back, not up to 5 minutes later.
 *
 * Reaction, least disruptive first: a persistent toast offers a manual refresh, and the next
 * client-side route change performs one automatically. Navigation is the safe reload point —
 * the user is leaving the view anyway, so there is no half-filled form to destroy, which is
 * why this never hard-reloads in place on detection.
 */
export function AppUpdateWatcher() {
  const { pathname } = useLocation();
  const updateReady = useRef(false);
  const lastPathname = useRef(pathname);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    let cancelled = false;
    const check = async () => {
      // Skip while hidden: a background tab doesn't need to know yet, and the
      // visibilitychange listener below re-checks the moment it matters.
      if (updateReady.current || document.hidden) return;
      const deployed = await fetchDeployedVersion();
      if (cancelled || updateReady.current || !deployed || deployed === __APP_VERSION__) return;
      updateReady.current = true;
      toast('A new version of DOP is available', {
        id: UPDATE_TOAST_ID,
        duration: Infinity,
        description: 'It will load when you move to another page — or refresh now.',
        action: { label: 'Refresh', onClick: () => window.location.reload() },
      });
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (!document.hidden) check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (pathname === lastPathname.current) return;
    lastPathname.current = pathname;
    // Pathname only, deliberately: search/hash changes are often filter state being typed into
    // the URL, mid-interaction — reloading there would interrupt exactly what the
    // navigation-based reload exists to avoid.
    if (updateReady.current) window.location.reload();
  }, [pathname]);

  return null;
}
